import {useState, type ReactNode, useRef} from "react";
import {createPortal} from "react-dom";
import {twJoin} from "tailwind-merge";

export type TooltipProps = {
    content: string;
    children: ReactNode;
    position?:
        | "top"
        | "bottom"
        | "left"
        | "right"
        | "top-start"
        | "top-end"
        | "bottom-start"
        | "bottom-end"
        | "left-start"
        | "left-end"
        | "right-start"
        | "right-end";
};

export const Tooltip = ({content, children, position = "bottom"}: TooltipProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const getCoordinates = () => {
        if (!wrapperRef.current) return {top: 0, left: 0};
        const rect = wrapperRef.current.getBoundingClientRect();

        switch (position) {
            case "top":
                return {
                    top: rect.top + window.scrollY - 8,
                    left: rect.left + window.scrollX + rect.width / 2,
                    transform: "translateX(-50%) translateY(-100%)",
                };

            case "top-start":
                return {
                    top: rect.top + window.scrollY - 8,
                    left: rect.left + window.scrollX,
                    transform: "translateY(-100%)",
                };

            case "top-end":
                return {
                    top: rect.top + window.scrollY - 8,
                    left: rect.right + window.scrollX,
                    transform: "translateX(-100%) translateY(-100%)",
                };

            case "bottom":
                return {
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX + rect.width / 2,
                    transform: "translateX(-50%)",
                };

            case "bottom-start":
                return {
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX,
                };

            case "bottom-end":
                return {
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.right + window.scrollX,
                    transform: "translateX(-100%)",
                };

            case "left":
                return {
                    top: rect.top + window.scrollY + rect.height / 2,
                    left: rect.left + window.scrollX - 8,
                    transform: "translateX(-100%) translateY(-50%)",
                };

            case "left-start":
                return {
                    top: rect.top + window.scrollY,
                    left: rect.left + window.scrollX - 8,
                    transform: "translateX(-100%)",
                };

            case "left-end":
                return {
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX - 8,
                    transform: "translateX(-100%) translateY(-100%)",
                };

            case "right":
                return {
                    top: rect.top + window.scrollY + rect.height / 2,
                    left: rect.right + window.scrollX + 8,
                    transform: "translateY(-50%)",
                };

            case "right-start":
                return {
                    top: rect.top + window.scrollY,
                    left: rect.right + window.scrollX + 8,
                };

            case "right-end":
                return {
                    top: rect.bottom + window.scrollY,
                    left: rect.right + window.scrollX + 8,
                    transform: "translateY(-100%)",
                };

            default:
                return {
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX + rect.width / 2,
                    transform: "translateX(-50%)",
                };
        }
    };

    const coordinates = showTooltip ? getCoordinates() : {top: 0, left: 0};

    return (
        <>
            <div
                ref={wrapperRef}
                className="inline-flex"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onFocus={() => setShowTooltip(true)}
                onBlur={() => setShowTooltip(false)}
                tabIndex={0}
            >
                {children}
            </div>

            {showTooltip &&
                createPortal(
                    <div
                        role="tooltip"
                        style={{
                            position: "absolute",
                            top: coordinates.top,
                            left: coordinates.left,
                            transform: coordinates.transform,
                        }}
                        className={twJoin(
                            "hidden sm:flex",
                            "transition-all duration-200 ease-out opacity-100",
                            "z-[9999] rounded-[3px] bg-ds-background-neutral-bold py-ds-space-025 px-ds-space-075 max-w-[240px] w-max whitespace-normal text-ds-text-inverse text-ds-font-body shadow-lg",
                        )}
                    >
                        {content}
                    </div>,
                    document.body,
                )}
        </>
    );
};
